import React from 'react';
import { View, Text, Pressable, StyleSheet, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import * as Haptics from 'expo-haptics';
import { notificationService } from '../services/notification-service';
import {
  OCPP_SERVER_URL,
  OCPP_SERVER_PORT,
  OCPP_SECURITY_PROFILE,
  OCPP_CA_CERTIFICATE_URL,
  buildOcppUrlWithId,
} from '../constants/ocpp-connection';

interface OcppConnectionInstructionsProps {
  /**
   * When the charger id is known (e.g. the serial the host registered with),
   * the explicit "URL with charger id" variant is shown. Otherwise a generic
   * `<chargerID>` placeholder is displayed.
   */
  chargerId?: string | null;
  /** Tighter spacing for embedding inside modals. */
  compact?: boolean;
  style?: object;
}

/**
 * Reusable, presentational walkthrough that tells a host how to point their
 * OCPP charger at the MeanderEV CSMS. Drop it into any surface (charger card,
 * registration screen, onboarding, activity log) so the instructions stay
 * identical everywhere. See `constants/ocpp-connection.ts` for the values.
 */
const OcppConnectionInstructions: React.FC<OcppConnectionInstructionsProps> = ({
  chargerId,
  compact = false,
  style,
}) => {
  const urlWithId = buildOcppUrlWithId(chargerId);

  const openCertificatePage = async () => {
    try {
      await Linking.openURL(OCPP_CA_CERTIFICATE_URL);
    } catch {
      notificationService.sendNotification('error', 'Could not open the certificate page');
    }
  };

  const copy = async (value: string, label: string) => {
    try {
      await Clipboard.setStringAsync(value);
      try {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      } catch {}
      notificationService.sendNotification('success', `${label} copied to clipboard`);
    } catch {
      notificationService.sendNotification('error', `Could not copy ${label.toLowerCase()}`);
    }
  };

  return (
    <View style={[styles.container, compact && styles.containerCompact, style]}>
      <View style={styles.headerRow}>
        <Ionicons name="hardware-chip-outline" size={18} color="#39BEB7" />
        <Text style={styles.headerText}>Connect your charger to MeanderEV</Text>
      </View>
      <Text style={styles.subText}>
        From your charger's own app or web portal, open its OCPP / CSMS settings
        and enter the details below.
      </Text>

      {/* Step 1 */}
      <View style={styles.step}>
        <View style={styles.stepBadge}>
          <Text style={styles.stepBadgeText}>1</Text>
        </View>
        <Text style={styles.stepText}>
          Go to the <Text style={styles.bold}>OCPP Connection / Connectivity</Text>{' '}
          section in your charger's configuration.
        </Text>
      </View>

      {/* Step 2 */}
      <View style={styles.step}>
        <View style={styles.stepBadge}>
          <Text style={styles.stepBadgeText}>2</Text>
        </View>
        <View style={styles.stepBody}>
          <Text style={styles.stepText}>
            Enter the <Text style={styles.bold}>MeanderEV Server URL</Text>:
          </Text>
          <Pressable
            style={({ pressed }) => [styles.copyChip, pressed && styles.copyChipPressed]}
            onPress={() => copy(OCPP_SERVER_URL, 'Server URL')}
            accessibilityRole="button"
            accessibilityLabel="Copy server URL"
          >
            <Text style={styles.copyChipText} selectable numberOfLines={1}>
              {OCPP_SERVER_URL}
            </Text>
            <Ionicons name="copy-outline" size={16} color="#39BEB7" />
          </Pressable>
        </View>
      </View>

      {/* Step 3 */}
      <View style={styles.step}>
        <View style={styles.stepBadge}>
          <Text style={styles.stepBadgeText}>3</Text>
        </View>
        <View style={styles.stepBody}>
          <Text style={styles.stepText}>
            Some chargers require the charger's ID in the URL. If yours does,
            use this instead:
          </Text>
          <Pressable
            style={({ pressed }) => [styles.copyChip, pressed && styles.copyChipPressed]}
            onPress={() => copy(urlWithId, 'Server URL')}
            accessibilityRole="button"
            accessibilityLabel="Copy server URL with charger ID"
          >
            <Text style={styles.copyChipText} selectable numberOfLines={1}>
              {urlWithId}
            </Text>
            <Ionicons name="copy-outline" size={16} color="#39BEB7" />
          </Pressable>
          {!chargerId && (
            <Text style={styles.hintText}>
              Replace {'<chargerID>'} with your charger's ID.
            </Text>
          )}
        </View>
      </View>

      {/* Settings summary */}
      <View style={styles.settingsBox}>
        <View style={styles.settingsRow}>
          <Text style={styles.settingsLabel}>Server Port</Text>
          <Text style={styles.settingsValue}>{OCPP_SERVER_PORT}</Text>
        </View>
        <View style={styles.settingsDivider} />
        <View style={styles.settingsRow}>
          <Text style={styles.settingsLabel}>Security</Text>
          <Text style={styles.settingsValue}>{OCPP_SECURITY_PROFILE}</Text>
        </View>
      </View>
      <Text style={styles.footnote}>
        If these aren't auto-selected, choose Port {OCPP_SERVER_PORT} and{' '}
        {OCPP_SECURITY_PROFILE}.
      </Text>

      {/* CA certificate */}
      <View style={styles.certSection}>
        <View style={styles.headerRow}>
          <Ionicons name="shield-checkmark-outline" size={18} color="#39BEB7" />
          <Text style={styles.headerText}>Charger asking for a CA certificate?</Text>
        </View>
        <Text style={styles.subText}>
          Some chargers require MeanderEV's server certificate to connect. Download it
          below, then upload it to your charger under{' '}
          <Text style={styles.bold}>Connectivity → OCPP Connection → Certificate</Text>.
        </Text>
        <Pressable
          style={({ pressed }) => [styles.copyChip, pressed && styles.copyChipPressed]}
          onPress={openCertificatePage}
          accessibilityRole="link"
          accessibilityLabel="Download the MeanderEV server certificate"
        >
          <Text style={styles.copyChipText} numberOfLines={1}>
            Download the MeanderEV server certificate
          </Text>
          <Ionicons name="open-outline" size={16} color="#39BEB7" />
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#1A1D23',
    borderRadius: 12,
    padding: 16,
    gap: 12,
  },
  containerCompact: {
    padding: 12,
    gap: 10,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
    flex: 1,
  },
  subText: {
    color: '#B5B7BA',
    fontSize: 13,
    lineHeight: 19,
  },
  step: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
  },
  stepBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#39BEB7',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  stepBadgeText: {
    color: '#0B1E1D',
    fontSize: 12,
    fontWeight: '800',
  },
  stepBody: {
    flex: 1,
    gap: 8,
  },
  stepText: {
    color: '#E8E8E9',
    fontSize: 14,
    lineHeight: 20,
    flex: 1,
  },
  bold: {
    fontWeight: '700',
    color: '#FFFFFF',
  },
  copyChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    backgroundColor: '#0B1E1D',
    borderWidth: 1,
    borderColor: '#39BEB7',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  copyChipPressed: {
    backgroundColor: '#13302E',
  },
  copyChipText: {
    color: '#39BEB7',
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },
  hintText: {
    color: '#8D8E90',
    fontSize: 12,
    fontStyle: 'italic',
  },
  settingsBox: {
    backgroundColor: '#0B1E1D',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  settingsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  settingsDivider: {
    height: 1,
    backgroundColor: '#292D33',
  },
  settingsLabel: {
    color: '#8D8E90',
    fontSize: 13,
    fontWeight: '600',
  },
  settingsValue: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
    flexShrink: 1,
    textAlign: 'right',
    marginLeft: 12,
  },
  footnote: {
    color: '#8D8E90',
    fontSize: 12,
    lineHeight: 17,
  },
  certSection: {
    borderTopWidth: 1,
    borderTopColor: '#292D33',
    paddingTop: 12,
    gap: 8,
  },
});

export default OcppConnectionInstructions;
